import { TenantMemberDisplayData } from '../identity'
import { ClaimLimitPeriod } from '../incentive'
import { Recipient } from '../recognition'
import {
  DateRange,
  DateRangePresetEnum,
  PaginatedSearch,
  Percentage,
  WithPermissions,
} from '../shared'
import {
  BucketAggChart,
  MemberChartData,
  TeamChartData,
  TimeAggChart,
} from '../shared/charts/types'
import { ListEngagementParams } from './engagement'
import { ListManagerLeaderboardParams } from './managerLeaderboard'

export type ReportType =
  | 'redemptionSummary'
  | 'awardSummary'
  | 'engagement'
  | 'managerLeaderboard'
  | 'recognitionActivity'
  | 'liabilities'
  | 'engagementActivity'
  | 'incentivesActivity'
  | 'pointsTransactions'
  | 'redemptionTransactions'

/**
 * The type/duration of a reporting interval. If a number is supplied,
 * it represents the number of days
 */
export type IntervalDuration = 'm' | '3m' | 'y' | number

export type PreviousCurrentPercent = {
  duration: IntervalDuration
  current: {
    value: number
    total: number
    percent: Percentage
    dateRange: DateRange
    activated?: number
    invited?: number
  }
  previous: {
    value: number
    total: number
    percent: Percentage
    dateRange: DateRange
  }
  diff: {
    value: number
    percent: Percentage
  }
}

export type PreviousCurrentCount = {
  duration: IntervalDuration
  current: {
    value: number
    dateRange: DateRange
  }
  previous: {
    value: number
    dateRange: DateRange
  }
  diff: {
    value: number
    percent: Percentage
  }
}

export type Currency = {
  value: number
  currency: string
}

export type ProviderCurrency = {
  cents: number
  currency: string
  points: number
  total: number
}

export type TopRedemption = {
  name: string
  value: number
}

export type ProviderCurrencyTotalsData = {
  currencies: Currency[]
  points: number
  providerCurrencies: { currencies: ProviderCurrency[]; id: string; name: string }[]
  total: number
}

export type RedemptionPointsCount = {
  pointsPerRedemption: number
  pointsPerEmployee: number
}

export type AllReportViewer = {
  name: string
  id: string
}

export enum ReportCategoryEnum {
  RECOGNITION = 'recognition',
  ENGAGEMENT = 'engagement',
  POINTS = 'points',
}

export type Report = WithPermissions<{
  type: ReportType
  category: ReportCategoryEnum
  foreign: boolean
  // embeds
  name?: string
  reportViewerCount?: number
  description?: string
  favourite?: boolean
  __entity_type__: 'report'
}>

export type FavouriteReportParams = {
  favourite: boolean
}

export type ReportViewer = {
  id: string
  reportType: ReportType
  tenantId: string
  viewerId: string
  createdAt: string
  updatedAt: string
  // embeds
  memberDisplayData?: TenantMemberDisplayData
  createdBy?: TenantMemberDisplayData
  __entity_type__: 'reportViewer'
}

export type RedemptionSummaryType =
  | 'averageSpending'
  | 'redemptionTypeAgg'
  | 'topRedemptions'
  | 'providerCurrencyTotals'

export type RedemptionSummaryData =
  | RedemptionPointsCount
  | TimeAggChart
  | TopRedemption[]
  | ProviderCurrencyTotalsData

export type RedemptionSummary = {
  type: RedemptionSummaryType
  data: RedemptionSummaryData
}

export enum ReportingIntervalEnum {
  LAST_YEAR = DateRangePresetEnum.LAST_YEAR,
  CURRENT_YEAR = DateRangePresetEnum.CURRENT_YEAR,
  LAST_3_MONTHS = DateRangePresetEnum.LAST_3_MONTHS,
  LAST_MONTH = DateRangePresetEnum.LAST_MONTH,
  CURRENT_MONTH = DateRangePresetEnum.CURRENT_MONTH,
}

export type ReportFilterParams =
  | ListEngagementParams
  | ListRedemptionSummaryParams
  | ListAwardSummaryParams
  | ListManagerLeaderboardParams

export type ListRedemptionSummaryParams = {
  dateRange?: string[]
  dateRangePreset?: string
  memberIdIn?: string[]
  teamIdIn?: string[]
  indirectManagerIdIn?: string[]
  ownReports?: boolean
  typeIn?: string[]
}

export type ReportEmbeds = 'name' | 'reportViewerCount' | 'description' | 'favourite'

export type ReportViewerEmbeds = 'members' | 'createdBy'

export type ListReportVeiwersParams = PaginatedSearch<object, ReportViewerEmbeds>

export type ListReportsParams = {
  legacyMode?: boolean
  embeds?: ReportEmbeds[]
}

export type AwardSummaryType =
  | 'totalAwardPoints'
  | 'averageAwardPoints'
  | 'totalAwards'
  | 'pointsPerBankChart'
  | 'topAwardRecipients'
  | 'topAwardTeams'

export type CurrentCount = {
  value: number
}

export type PointsPerBankChartData = BucketAggChart

export type AwardSummaryData =
  | CurrentCount
  | PointsPerBankChartData
  | MemberChartData[]
  | TeamChartData[]

export type AwardSummary = {
  type: AwardSummaryType
  data: AwardSummaryData
}

export type ListAwardSummaryParams = Omit<ListRedemptionSummaryParams, 'typeIn'> & {
  pointsBankIdIn?: string[]
}

export enum ScheduledEmailReportFrequency {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  WEEKLY = 'weekly',
}

export type ScheduledEmailReport = {
  email: string
  frequency: ScheduledEmailReportFrequency
  reportType: ReportType
  tenantId: string
  createdById: string
  createdAt: Date
  // embeds
  nextSendAt?: Date
  createdBy?: TenantMemberDisplayData
  __entity_type__: 'scheduledEmailReport'
}

export type ScheduledEmailReportEmbeds = 'createdBy' | 'nextSendAt'

export type ListScheduledEmailReportsParams = PaginatedSearch<
  {
    frequencyIn?: ScheduledEmailReportFrequency[]
  },
  ScheduledEmailReportEmbeds
>

export type EmailedReportsEmbeds = 'sharedBy' | 'name'

export type ListEmailedReportsParams = PaginatedSearch<object, EmailedReportsEmbeds>

export type DeleteScheduledEmailParams = {
  emails: string[]
}

export type DateRangePreset =
  | DateRangePresetEnum.LAST_ISO_WEEK
  | DateRangePresetEnum.LAST_MONTH
  | DateRangePresetEnum.LAST_3_MONTHS

export type EmailedReport = {
  id: string
  email: string
  reportType: ReportType
  tenantId: string
  mediaObjectId: string | null
  secret: string
  sentById: string
  scheduled: boolean
  batchId: string
  startDate: string
  endDate: string
  dateRangePreset: DateRangePreset
  createdAt: string
  updatedAt: string
  // embeds
  sharedBy: string
  __entity_type__: 'emailedReport'
}

export type ScheduleEmailParams = {
  emails: string
  frequency: ScheduledEmailReportFrequency
}

export type SendEmailParams = {
  emails: string
  dateRangePreset: DateRangePreset
}

export type CreateViewersParams = {
  memberIds: string[]
}

type ActivityTenantMember = {
  id: string
  active: boolean
  email: string
  firstName: string
  lastName: string
  departments: {
    name: string
  }[]
  employeeId: string | null
  locations: {
    country: string
    name: string
  }[]
  manager: {
    firstName: string
    lastName: string
  } | null
}

export type RecognitionRecipientActivity = Recipient & {
  cursor: string | null
  values: string[]
  qualities: string[]
  delegate: {
    id: string
    name: string
  }
  message: string
  points: number
  publishedAt: string
  private: boolean
  hasAttachments: boolean
  sender?: ActivityTenantMember
  recipient?: ActivityTenantMember
}

export type ListRecognitionRecipientActivityParams = {
  cursor?: string | null
  pageSize?: number
  senderIdIn?: string[]
  recipientIdIn?: string[]
  recipientTeamIdIn?: string[]
  recipientManagerIdIn?: string[]
  publishedInDateRange?: string[]
}

export type ListEngagementActivityParams = {
  cursor?: string | null
  pageSize?: number
  idIn?: string[]
  managerIdIn?: string[]
  teamIdIn?: string[]
  suspended?: boolean
  dateRange?: string[]
}

export type EngagementActivityMemberData = ActivityTenantMember & {
  cursor?: string | null
  data: {
    [key: string]: number
  }
}

export type ListIncentiveActivityParams = {
  cursor?: string | null
  pageSize?: number
  idIn?: string[]
  bugetItemIdIn?: string[]
  activeInDateRange?: string[]
  sorting?: string[]
}

export type IncentiveActivityData = {
  cursor: string | null
  id: string
  title: string
  claimLimit: number | null
  claimLimitPeriod: ClaimLimitPeriod | null
  budgetItemId: string | null
  startAt: string
  endAt: string | null
  participants: number
  participationRate: number | null
  audienceSize: number
  approvedClaims: number
  pendingClaims: number
  deniedClaims: number
  approvalRate: number | null
  pointsPerParticipant: number
  points: number
  pointsPerClaim: number
  pointsBankName: string | null
}
